import { inject, OnDestroy } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subscription } from "rxjs";

import { Types } from "../../../domain/types.domain";
import { City } from "../../../domain/city/entities/city.entity";
import { resolveBrandName } from "../../utils/brand-name";

type TitleFn<T> = (component: T) => string | Observable<string>;

/**
 * Título da aba da página.
 *
 * Forma string: `pageName` é só o nome da página ("Login", "Criar conta"); o
 * sufixo " - <marca>" é resolvido aqui a partir da cidade da rota e de
 * `settings.brandedPageTitle` (APR-3501), "Aprova" por padrão.
 * Forma função: o título vem inteiro do chamador, sem sufixo.
 */
export function PageTitleComponent(pageName: string, fallbackTitle?: string): new () => OnDestroy;
export function PageTitleComponent<T>(title: TitleFn<T>, fallbackTitle?: string): new () => OnDestroy;

export function PageTitleComponent<T>(title: string | TitleFn<T>, fallbackTitle?: string) {
  return class implements OnDestroy {
    private readonly _title = inject(Title);
    private readonly _route = inject(ActivatedRoute);
    private readonly _settings = inject(Types.Settings);
    private currentTitle: string;
    private titleSubscription?: Subscription;

    constructor() {
      this.currentTitle = this._title.getTitle();
      setTimeout(() => this.setPageTitle());
    }

    private setPageTitle() {
      if (typeof title === 'function') {
        const titleValue = title(this as any);

        if (titleValue instanceof Observable) {
          this.titleSubscription = titleValue.subscribe((title) => {
            this._title.setTitle(title);
          });
        } else {
          this._title.setTitle(titleValue);
        }

        return;
      }

      // Preenchido pelo cityResolver na rota-pai `:city` e herdado pelas filhas
      // (paramsInheritanceStrategy 'always'); rota sem cidade → undefined.
      const city = this._route.snapshot.data['city'] as City | null | undefined;
      const brand = resolveBrandName(city, this._settings.brandedPageTitle);

      this._title.setTitle(`${title} - ${brand}`);
    }

    ngOnDestroy(): void {
      this._title.setTitle(fallbackTitle ?? this.currentTitle);
      this.titleSubscription?.unsubscribe();
    }
  }
}
